import { z } from "zod";
import { isModelSetRef, MODEL_SET_PREFIX } from "./models.js";
import { RUNTIME_PROVIDERS, RuntimeProviderSchema } from "./runtime.js";
/**
 * Subscription-commerce contracts (billing refactor, 2026-07-28). The legacy
 * PlanOffering/BillingSummary DTOs were removed at the atomic cutover along
 * with their routes and tables — no compatibility layer.
 *
 * Two hard rules govern every shape here:
 * - Stripe identifiers never appear. Plan ids are stable site-scoped codes,
 *   offer/selection/change ids are opaque internal ids.
 * - Presentation is never permission. Entitlements are the enforced facts;
 *   presentation records are marketing copy the server localizes.
 */
// --- Entitlement registry ----------------------------------------------------
/**
 * The closed set of entitlement keys the application understands. The DB's
 * billing_entitlement_definitions rows are validated against this registry;
 * an unknown or missing key fails closed (refuse to compute a policy) rather
 * than guessing in either direction.
 */
export const ENTITLEMENT_KEYS = [
    "models.allowed",
    "tools.disabled",
    "mcp.enabled",
    "web_fetch.enabled",
    "hitl.required",
    "runs.daily_cap",
    "profiles.max",
    "credits.allowance",
    "credits.reset_cadence",
    // Template Strategy V2 phase 4c (migration 110): which runtime providers a
    // plan may reserve on (intersects with every other layer) and an optional
    // plan-level preference order over the site's routing order (null = the
    // site order). Closed keys, never free-form.
    "runtime.providers_allowed",
    "runtime.provider_order",
];
export const EntitlementKeySchema = z.enum(ENTITLEMENT_KEYS);
/**
 * FROZEN: the nine-key vocabulary migration 080's embedded catalog snapshot
 * was rendered with (catalog-manifest-v1.ts + catalog-snapshot.ts). It exists
 * only so that byte-parity anchor keeps rendering forever; live code uses
 * ENTITLEMENT_KEYS. Never extend.
 */
export const ENTITLEMENT_KEYS_V1 = [
    "models.allowed",
    "tools.disabled",
    "mcp.enabled",
    "web_fetch.enabled",
    "hitl.required",
    "runs.daily_cap",
    "profiles.max",
    "credits.allowance",
    "credits.reset_cadence",
];
/**
 * One `models.allowed` entry: a model id, or a registry-defined set reference
 * `set:<id>` (models.ts MODEL_SETS). Literal ids are NOT checked against the
 * catalog (a sealed legacy plan may name a model the catalog no longer lists —
 * it simply never matches), but an unknown set is a parse failure: refuse to
 * compute a policy rather than guess what a set means.
 */
export const ModelRefSchema = z
    .string()
    .min(1)
    .refine((value) => !value.startsWith(MODEL_SET_PREFIX) || isModelSetRef(value), {
    message: "unknown model set reference",
});
const distinctProviders = (values) => new Set(values).size === values.length;
/**
 * A plan version's complete entitlement record as stored (dotted keys).
 * `.strict()` + all-required is the fail-closed contract: an unknown key or a
 * missing key is a hard parse failure, never a silent default. null caps mean
 * unlimited, never zero — and a missing key never means unlimited.
 */
export const PlanEntitlementsRecordSchema = z
    .object({
    "models.allowed": z.array(ModelRefSchema).min(1),
    "tools.disabled": z.array(z.string().min(1)),
    "mcp.enabled": z.boolean(),
    "web_fetch.enabled": z.boolean(),
    "hitl.required": z.boolean(),
    "runs.daily_cap": z.number().int().positive().nullable(),
    "profiles.max": z.number().int().positive().nullable(),
    "credits.allowance": z.number().int().nonnegative().nullable(),
    "credits.reset_cadence": z.literal("monthly"),
    "runtime.providers_allowed": z
        .array(RuntimeProviderSchema)
        .min(1)
        .refine(distinctProviders, { message: "duplicate provider" }),
    "runtime.provider_order": z
        .array(RuntimeProviderSchema)
        .refine(distinctProviders, { message: "duplicate provider" })
        .nullable(),
})
    .strict();
/** FROZEN nine-key record shape (see ENTITLEMENT_KEYS_V1) — the 080 snapshot anchor only. */
export const PlanEntitlementsRecordV1Schema = z
    .object({
    "models.allowed": z.array(z.string().min(1)).min(1),
    "tools.disabled": z.array(z.string().min(1)),
    "mcp.enabled": z.boolean(),
    "web_fetch.enabled": z.boolean(),
    "hitl.required": z.boolean(),
    "runs.daily_cap": z.number().int().positive().nullable(),
    "profiles.max": z.number().int().positive().nullable(),
    "credits.allowance": z.number().int().nonnegative().nullable(),
    "credits.reset_cadence": z.literal("monthly"),
})
    .strict();
/**
 * Transitional READ codec (Template Strategy V2 phase 4c). Plan versions are
 * immutable (trigger-sealed), so versions sealed BEFORE migration 110 can never
 * carry the two runtime keys — yet they stay reachable (a Stripe renewal
 * re-pins its offer's version, history reads, a publish-before-migrate
 * database). A complete NINE-key record is upgraded on read with exactly what
 * 110 wrote for every rolled plan — every provider allowed, no plan-level
 * order — the only meaning a pre-110 version can have (it could not express a
 * restriction). Everything else stays strict: a record missing any of the nine
 * keys, carrying only ONE of the two runtime keys, or carrying an unknown key
 * still fails closed. Like the launch-manifest v1–v5 upgrade this codec reads
 * forever and writes never (110 is the last roll; no row is ever rewritten).
 */
export function upgradePlanEntitlementsRecord(value) {
    if (!value || typeof value !== "object" || Array.isArray(value))
        return value;
    const record = value;
    if ("runtime.providers_allowed" in record || "runtime.provider_order" in record)
        return value;
    if (!PlanEntitlementsRecordV1Schema.safeParse(record).success)
        return value;
    return {
        ...record,
        "runtime.providers_allowed": [...RUNTIME_PROVIDERS],
        "runtime.provider_order": null,
    };
}
/** Fail-closed: throws on unknown keys, missing keys, or mistyped values (after the v1 read codec). */
export function parsePlanEntitlements(value) {
    const record = PlanEntitlementsRecordSchema.parse(upgradePlanEntitlementsRecord(value));
    return {
        modelsAllowed: record["models.allowed"],
        toolsDisabled: record["tools.disabled"],
        mcpEnabled: record["mcp.enabled"],
        webFetchEnabled: record["web_fetch.enabled"],
        hitlRequired: record["hitl.required"],
        runsDailyCap: record["runs.daily_cap"],
        profilesMax: record["profiles.max"],
        creditsAllowance: record["credits.allowance"],
        creditsResetCadence: record["credits.reset_cadence"],
        runtimeProvidersAllowed: record["runtime.providers_allowed"],
        runtimeProviderOrder: record["runtime.provider_order"],
    };
}
// --- Catalog -----------------------------------------------------------------
export const BillingIntervalSchema = z.enum(["month", "year"]);
/**
 * A sellable commercial offer. `id` is an opaque internal id — never a Stripe
 * price id. The localized `note` is per-offer marketing copy (e.g. the LYA
 * per-student framing); real savings claims live here, composed server-side,
 * never derived client-side.
 */
export const BillingOfferSchema = z.object({
    /** The offer code — also the Stripe Price lookup_key (plans.ts). Never a Stripe id. */
    id: z.string().min(1),
    interval: BillingIntervalSchema,
    amountCents: z.number().int().nonnegative(),
    currency: z.literal("usd"),
    note: z.string().nullable(),
});
/**
 * Badge slugs drive styling with a safe plain default for unknown slugs, so
 * an arbitrary future catalog renders without client changes. Labels arrive
 * localized.
 */
export const BillingBadgeSchema = z.object({
    slug: z.string().min(1),
    label: z.string().min(1),
});
/** Localized, server-driven presentation. Marketing only — never enforced. */
export const BillingPlanPresentationSchema = z.object({
    name: z.string().min(1),
    blurb: z.string(),
    badges: z.array(BillingBadgeSchema),
    featureGroups: z.array(z.object({ title: z.string().nullable(), items: z.array(z.string().min(1)) })),
    modelHighlights: z.array(z.object({ label: z.string().min(1), tone: z.string().nullable() })),
    comparisonLeadIn: z.string().nullable(),
});
/**
 * One plan in billing.catalog (V5: rendered from the `plans.ts` constants, no credits,
 * no plan versions). `id` is the plan id; the UI treats it as opaque presentation keying
 * and never branches on specific values. Everything enforced stays server-side.
 */
export const BillingCatalogPlanSchema = z.object({
    id: z.string().min(1),
    rank: z.number().int(),
    isDefault: z.boolean(),
    presentation: BillingPlanPresentationSchema,
    offers: z.array(BillingOfferSchema),
});
export const BillingCatalogSchema = z.object({
    plans: z.array(BillingCatalogPlanSchema),
});
// --- Account snapshot --------------------------------------------------------
/**
 * The exhaustive raw provider status projection ("none" = no Stripe
 * subscription). Cancellation is deliberately NOT a status here — it rides
 * the separate cancelAtPeriodEnd flag, mirroring Stripe's own model.
 */
export const BillingProviderStatusSchema = z.enum([
    "none",
    "incomplete",
    "incomplete_expired",
    "trialing",
    "active",
    "past_due",
    "canceled",
    "unpaid",
    "paused",
]);
export const BillingAccessStateSchema = z.enum(["free", "paid", "grace"]);
export const BillingChangeKindSchema = z.enum([
    "initial_purchase",
    "upgrade",
    "downgrade",
    "cadence_change",
    "move_to_free",
    "cancel",
]);
/**
 * Lifecycle of a durable commerce change/selection. The client's post-return
 * polling terminates only on applied | failed | canceled | expired |
 * superseded for ITS correlation id, as billing.selection reads it — an
 * already-active subscription is not convergence.
 */
export const BillingChangeStatusSchema = z.enum([
    "pending",
    "processing",
    "scheduled",
    "applied",
    "failed",
    "canceled",
    "expired",
    "superseded",
]);
export const BillingSubscriptionChangeSchema = z.object({
    id: z.string().min(1),
    kind: BillingChangeKindSchema,
    status: BillingChangeStatusSchema,
    effectiveAt: z.string().nullable(),
    targetPlanName: z.string().nullable(),
    targetOffer: BillingOfferSchema.nullable(),
});
/**
 * Server-computed transition the viewer may request right now. The UI never
 * derives upgrade/downgrade direction from prices or ranks — it renders
 * exactly this list.
 */
export const BillingTransitionSchema = z.object({
    offerId: z.string().min(1),
    kind: BillingChangeKindSchema,
    timing: z.enum(["immediate", "period_end"]),
});
/**
 * billing.account. Everything the billing surfaces render comes from here;
 * Checkout/Portal return query params never change what renders. V5: the plan is derived
 * from the live subscription and the plan constants (no assignments, no credits).
 */
/**
 * Where the effective plan came from: the paid/grace subscription, the admin's own stored
 * test plan (access.ts: consulted only without a live subscription row, only while the
 * account still holds platform_admin), or the site's default — which a live row can also
 * yield (quarantined, no entitled offer), so `default` never implies "no subscription".
 */
export const PlanSourceSchema = z.enum(["subscription", "admin_override", "default"]);
export const BillingAccountSnapshotSchema = z.object({
    canManageBilling: z.boolean(),
    // false while the Stripe subsystem is absent/disabled for this site; the UI
    // shows paid actions as unavailable instead of issuing doomed requests.
    commerceAvailable: z.boolean(),
    plan: z.object({
        id: z.string().min(1),
        name: z.string().min(1),
        rank: z.number().int(),
        // Server-computed: replaces every client `currentPlan === "free"` check.
        isFreeTier: z.boolean(),
    }),
    // Where `plan` came from. The pricing cards treat a plan as OWNED only on
    // `subscription`; under `admin_override` every paid offer stays purchasable.
    planSource: PlanSourceSchema,
    // The last invoice-paid entitled offer — null on the default plan.
    entitledOffer: BillingOfferSchema.nullable(),
    accessState: BillingAccessStateSchema,
    providerStatus: BillingProviderStatusSchema,
    cancelAtPeriodEnd: z.boolean(),
    // Stripe billing period (from the base subscription item); null when free.
    paymentPeriod: z.object({ start: z.string(), end: z.string() }).nullable(),
    grace: z.object({ until: z.string() }).nullable(),
    pendingChange: BillingSubscriptionChangeSchema.nullable(),
    pendingSelectionId: z.string().min(1).nullable(),
    lastAppliedSelectionId: z.string().min(1).nullable(),
    availableTransitions: z.array(BillingTransitionSchema),
});
// --- Commands ----------------------------------------------------------------
/**
 * billing.selectOffer. requestId is a REQUIRED client-generated UUID (the durable
 * selection's idempotency identity); V5 also REQUIRES the Idempotency-Key header and it
 * must equal this value (V2 made the header optional).
 */
export const OfferSelectionRequestSchema = z.object({
    offerId: z.string().min(1),
    requestId: z.uuid(),
});
export const OfferSelectionResponseSchema = z.discriminatedUnion("outcome", [
    // Checkout or Portal update-confirm URL; selectionId is the opaque
    // correlation id the return-URL polling converges on.
    z.object({
        outcome: z.literal("redirect"),
        url: z.string().url(),
        selectionId: z.string().min(1),
    }),
    z.object({ outcome: z.literal("scheduled"), change: BillingSubscriptionChangeSchema }),
    z.object({ outcome: z.literal("processing"), selectionId: z.string().min(1) }),
    z.object({ outcome: z.literal("unchanged") }),
]);
/**
 * billing.selection: where ONE selection stands, by the opaque selectionId a redirect or
 * processing answer named (the return URL's `?selection=`). The status is that selection's
 * own change, whatever it is now: the snapshot names only the open selection and the last
 * applied one, so a selection that failed, expired, was canceled or superseded, or one a
 * newer purchase followed, is read here.
 */
export const BillingSelectionStatusSchema = z.object({
    selectionId: z.string().min(1),
    status: BillingChangeStatusSchema,
});
export const PortalSessionResponseSchema = z.object({ url: z.string().url() });
//# sourceMappingURL=billing.js.map